// src/oura/overview/OverviewTiles.tsx
//
// The four Overview tiles: one number per place a customer finds the business,
// the site under them, and this month's work. Each tile opens the screen that
// carries the detail; the cards those screens now hold used to sit here.

import { Fragment, type ReactNode } from "react";
import { ActivityIcon, BotIcon, ClipboardListIcon, SearchIcon } from "lucide-react";
import type { AeoCrawlAbsence, AeoSearchPerformance } from "@service/aeo";
import type { BusinessAudit } from "../audit";
import type { GscState } from "../gscState";
import type { Places } from "../places";
import { Delta, LATE_TEXT, OfValue, Tile, Tiles } from "@oura/ui";
import { cn } from "../lib/cn";
import type { TrendDelta } from "../visibility/model";
import { dayMonth } from "../visibility/format";
import { count, staffHref, type Link, type TopRival } from "./model";

/** What the This month tile can say. `off` when the client-review service may not be read. */
export type MonthTileState =
    | { kind: "off" }
    | { kind: "loading" }
    /** The client's page could not be read (network, 401, 403). */
    | { kind: "failed" }
    /** Nothing has been sold, so there is no month to count. */
    | { kind: "none" }
    /**
     * This phase's deliverables on the client's page, and our questions to
     * the client now late. `done` and `total` count measured lines only;
     * `byHand` is how many `reported_by_hand` lines were left out of both
     * (staging 3fdc24e6).
     */
    | { kind: "plan"; done: number; total: number; byHand: number; lateQuestions: number };

/** Why Site health has no number, short enough for a tile. Never a 0. */
const SITE_HEALTH_ABSENT: Record<AeoCrawlAbsence, string> = {
    crawl_failed: "The crawl did not finish on this scan",
    crawl_blocked: "Every page came back unreadable",
    not_crawled: "The crawl read nothing to score",
};

/** Parts of a sub-line, joined with " · ". */
function joined(parts: ReactNode[]): ReactNode {
    if (!parts.length) return null;
    return parts.map((p, i) => (
        <Fragment key={i}>
            {i > 0 && " · "}
            {p}
        </Fragment>
    ));
}

/** The tile's count: Visibility's headline, so the two screens state one number. */
export interface AiHeadline {
    named: number;
    of: number;
    /** Why there is no count, in words, when `of` is 0. */
    absent: string | null;
}

function AiTile({ href, headline, change, rival }: { href: (l: Link) => string; headline: AiHeadline; change: TrendDelta | null; rival: TopRival | null }) {
    const icon = { icon: BotIcon, area: "ai" } as const;
    if (headline.of === 0) {
        return <Tile label="AI assistants" icon={icon} value="Not measured" sub={headline.absent} to={href({ view: "questions" })} />;
    }
    const parts: ReactNode[] = [];
    // A change across a question-set change is not a change: Delta draws
    // nothing then, and the separator must not be left hanging either.
    if (change?.comparable) {
        const since = dayMonth(change.since);
        parts.push(<Delta value={change.value} comparable={change.comparable} suffix={since ? `since ${since}` : undefined} />);
    }
    if (rival) {
        // "takes 8" never wraps away from itself, so the count stays beside its verb.
        parts.push(
            <span title={`Named on ${count(rival.count, "question")} where this business was not`}>
                {rival.name} <span className="whitespace-nowrap">takes {rival.count}</span>
            </span>,
        );
    }
    return (
        <Tile
            label="AI assistants"
            icon={icon}
            value={<OfValue n={headline.named} of={headline.of} />}
            sub={joined(parts)}
            to={href({ view: "questions" })}
        />
    );
}

function GoogleTile({ href, places, search, gsc }: { href: (l: Link) => string; places: Places; search: AeoSearchPerformance | null | undefined; gsc: GscState | undefined }) {
    const g = places.google;
    const icon = { icon: SearchIcon, area: "google" } as const;
    if (!g.connected) {
        // The scan carries no Search Console block. The catalog says whether
        // that is a missing connection or one that has not reached this scan,
        // and "Not connected" over a connection the Connectors page shows as
        // live would send the reader to fix the wrong thing.
        const linked = !!gsc && gsc.kind !== "not_connected";
        return (
            <Tile
                label="Google search"
                icon={icon}
                value={linked ? "Not measured" : "Not connected"}
                sub={linked ? gsc.label : "Connect Search Console"}
                to={href({ view: "connectors" })}
            />
        );
    }
    if (g.avgPosition === null) {
        return <Tile label="Google search" icon={icon} value="No position yet" sub="Search Console reports with a 2 day lag" to={href({ view: "search" })} />;
    }
    const days = search?.window?.days;
    return (
        <Tile
            label="Google search"
            icon={icon}
            value={<OfValue n={g.avgPosition.toFixed(1)} unit="avg position" />}
            sub={g.clicks !== null
                ? `${g.clicks.toLocaleString("en-GB")} clicks${days ? `, ${days} days` : ""}`
                : g.top10 !== null
                    ? `${count(g.top10, "search", "searches")} in the top 10`
                    : null}
            to={href({ view: "search" })}
        />
    );
}

function SiteTile({ href, audit, readabilityMeasured }: { href: (l: Link) => string; audit: BusinessAudit; readabilityMeasured: boolean }) {
    const icon = { icon: ActivityIcon, area: "site" } as const;
    // A live scan can arrive with no number; the type says otherwise.
    const score: number | null = typeof audit.siteHealthScore === "number" ? audit.siteHealthScore : null;
    if (score === null) {
        return (
            <Tile
                label="Site health"
                icon={icon}
                value="Not measured"
                sub={(audit.siteHealthScoreAbsent && SITE_HEALTH_ABSENT[audit.siteHealthScoreAbsent]) || "The crawl produced nothing to score"}
                to={href({ view: "setup" })}
            />
        );
    }
    // The band is the first word of the backend's verdict ("Weak AI
    // readability, high upside"), never a band worked out here.
    const band = audit.aiReadabilityVerdict?.trim().split(/\s+/)[0]?.toLowerCase();
    const readability = typeof audit.aiReadabilityScore === "number"
        ? `AI readability ${audit.aiReadabilityScore}${band ? `, ${band}` : ""}`
        : readabilityMeasured
            ? "AI readability not measured"
            : `${count(audit.pagesCrawled, "page")} crawled`;
    return <Tile label="Site health" icon={icon} value={<OfValue n={score} unit="/ 100" />} sub={readability} to={href({ view: "setup" })} />;
}

/**
 * This phase's deliverables, from the client's page. It opens the contract,
 * where those deliverables are listed; with the client-review service shut it
 * points at Fixes, the plan staff can still read.
 */
function MonthTile({ href, month }: { href: (l: Link) => string; month: MonthTileState }) {
    const base = { label: "This month", icon: { icon: ClipboardListIcon, area: "work" } as const };
    const contract = href({ view: "contract" });
    switch (month.kind) {
        case "loading":
            return <Tile {...base} value={null} loading to={contract} />;
        case "off":
            return <Tile {...base} value="–" sub="The plan is on Fixes" to={href({ view: "todo" })} />;
        case "failed":
            return <Tile {...base} value="–" sub="The client's page could not be read" to={contract} />;
        case "none":
            return <Tile {...base} value="–" sub="No plan sold yet" to={contract} />;
        case "plan": {
            // Late questions show whichever way the month is counted, "By hand" included.
            // The Client tab's "Questions to the client": what we asked them, never what they asked us.
            const late = month.lateQuestions > 0 && (
                <span className={cn(LATE_TEXT, "whitespace-nowrap")}>{count(month.lateQuestions, "question")} to the client late</span>
            );
            if (!month.total) {
                return month.byHand > 0 ? (
                    <Tile
                        {...base}
                        value="By hand"
                        sub={late ? joined(["Accounted for by hand", late]) : "This month's work is accounted for by hand"}
                        to={contract}
                    />
                ) : (
                    <Tile {...base} value="–" sub="No deliverables this month" to={contract} />
                );
            }
            return (
                <Tile
                    {...base}
                    value={<OfValue n={month.done} of={month.total} />}
                    sub={late ? joined(["Deliverables done", late]) : "Deliverables done this month"}
                    ariaLabel={`This month: ${month.done} of ${count(month.total, "deliverable")} done`}
                    to={contract}
                />
            );
        }
    }
}

export function OverviewTiles({
    businessId,
    headline,
    places,
    change,
    rival,
    audit,
    readabilityMeasured,
    search,
    gsc,
    month,
}: {
    businessId: string;
    headline: AiHeadline;
    /** Google search reads its place from here. */
    places: Places;
    /** Named now against the scan before, through Visibility's own trend. */
    change: TrendDelta | null;
    rival: TopRival | null;
    audit: BusinessAudit;
    /** AI readability is part of this kind of scan, so its absence is worth saying. */
    readabilityMeasured: boolean;
    search: AeoSearchPerformance | null | undefined;
    gsc: GscState | undefined;
    /** Null on a past scan: the month is today's, not that scan's, so the tile is left out. */
    month: MonthTileState | null;
}) {
    const href = (l: Link) => staffHref(businessId, l);
    return (
        <Tiles cols={month ? 4 : 3}>
            <AiTile href={href} headline={headline} change={change} rival={rival} />
            <GoogleTile href={href} places={places} search={search} gsc={gsc} />
            <SiteTile href={href} audit={audit} readabilityMeasured={readabilityMeasured} />
            {month && <MonthTile href={href} month={month} />}
        </Tiles>
    );
}
